import React, {createContext, useCallback, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import styled from 'styled-components';
import {Z_INDEXES} from '../../../constants/layers';
import {useToastPortalElement} from '../../../hooks/usePortalElement/useToastPortalElement';
import {Toast} from './Toast';
import {ToastProps as BaseToastProps} from './types';

type ToastProps = Omit<BaseToastProps, 'animationDuration'>;

interface ToastPropsWithUUI extends ToastProps {
  uuid: number;
}

interface ProviderProps {
  maxToasts?: number;
}

export const ToastContext = createContext<{
  showToast: (toast: ToastProps) => void;
  removeAllToasts: VoidFunction;
}>({
  showToast: () => void 0,
  removeAllToasts: () => void 0,
});

export const ToastContextProvider: React.FC<
  React.PropsWithChildren<ProviderProps>
> = ({
  // should add prop for max number of toasts displayed
  children,
  maxToasts,
}) => {
  const [toasts, setToasts] = useState<ToastPropsWithUUI[]>([]);
  const toastPortalElement = useToastPortalElement();

  const removeToast = (toastUuid: number) => {
    setToasts(toasts.filter(toast => toast.uuid !== toastUuid));
  };

  const showToast = useCallback(
    (toast: ToastProps) => {
      // if the length of the currently stored toasts plus the newly added one is greater than the maxToasts we will remove the first toast from the array
      const updatedToasts =
        maxToasts && toasts.length + 1 > maxToasts ? toasts.slice(1) : toasts;

      // we need to add a uuid to each toast in order to be able to remove them properly
      const appendedToasts = [
        ...updatedToasts,
        {...toast, uuid: performance.now()},
      ];
      setToasts(appendedToasts);
    },
    [toasts, maxToasts],
  );

  const removeAllToasts = () => {
    setToasts([]);
  };

  const value = useMemo(() => ({showToast, removeAllToasts}), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toastPortalElement &&
        createPortal(
          <ToastsContainer>
            {toasts.map(toast => (
              <Toast
                {...toast}
                key={toast.uuid}
                onDismiss={() => {
                  toast.onDismiss?.();
                  removeToast(toast.uuid);
                }}
                onAutoClose={() => {
                  toast.onAutoClose?.();
                  removeToast(toast.uuid);
                }}
              />
            ))}
          </ToastsContainer>,
          toastPortalElement,
        )}
    </ToastContext.Provider>
  );
};

const ToastsContainer = styled.div`
  z-index: ${Z_INDEXES.TADARIDA_TOAST_CONTAINER};
  position: absolute;
`;
